import React, { useState, useEffect } from 'react';
import {
    Box, Typography, CardMedia, Container, Alert, 
    CircularProgress, Chip, Divider, Button, IconButton, Card, CardContent
} from '@mui/material';
import axios from 'axios';

// Icons
import EventIcon from '@mui/icons-material/Event';
import ImageIcon from '@mui/icons-material/Image';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NewReleasesIcon from '@mui/icons-material/NewReleases';

const UpdatesAndTipsViewer = () => {
    const [updates, setUpdates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedUpdate, setSelectedUpdate] = useState(null);
    const [expandedCards, setExpandedCards] = useState({});
    const [carouselPositions, setCarouselPositions] = useState({});

    const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
    const CONTENT_LIMIT = 300;

    useEffect(() => {
        const fetchUpdates = async () => {
            try {
                const token = localStorage.getItem('jwtToken');
                const res = await axios.get(`${API_BASE_URL}/api/updates-tips`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUpdates(res.data);
            } catch (err) {
                console.error("Failed to fetch updates.", err);
                setError('Failed to load updates. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchUpdates();
    }, [API_BASE_URL]);

    const isNew = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= 7;
    };

    const toggleExpanded = (updateId) => {
        setExpandedCards(prev => ({
            ...prev,
            [updateId]: !prev[updateId]
        }));
    };

    const shouldTruncate = (content) => {
        return content && content.length > CONTENT_LIMIT;
    };

    const getTruncatedContent = (content) => {
        if (!content) return '';
        return content.substring(0, CONTENT_LIMIT) + '...';
    };

    const scrollCarousel = (updateId, direction) => {
        const container = document.getElementById(`carousel-${updateId}`);
        if (container) {
            const scrollAmount = container.offsetWidth;
            container.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    const handleCarouselScroll = (updateId, event) => {
        const container = event.target;
        const position = container.scrollLeft;
        const maxScroll = container.scrollWidth - container.offsetWidth;
        
        setCarouselPositions(prev => ({
            ...prev,
            [updateId]: {
                atStart: position <= 0,
                atEnd: position >= maxScroll - 1
            }
        }));
    };

    const handleBackClick = () => {
        setSelectedUpdate(null);
        window.scrollTo(0, 0);
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress sx={{ color: '#11224d' }} />
            </Box>
        );
    }

    // --- DETAIL VIEW ---
    if (selectedUpdate) {
        const mediaList = selectedUpdate.media || (selectedUpdate.media_url ? [{ media_url: selectedUpdate.media_url, media_type: selectedUpdate.media_type }] : []);
        const hasMultipleMedia = mediaList.length > 1;
        const carouselPos = carouselPositions[selectedUpdate.id] || { atStart: true, atEnd: false };
        const isExpanded = expandedCards[selectedUpdate.id];
        const showReadMore = shouldTruncate(selectedUpdate.content);
        const displayContent = showReadMore && !isExpanded 
            ? getTruncatedContent(selectedUpdate.content) 
            : selectedUpdate.content;

        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBackClick}
                    sx={{ mb: 3, textTransform: 'none', fontWeight: 700, color: '#fff', bgcolor: '#11224d', borderRadius: 2, px: 3, py: 1, '&:hover': { bgcolor: '#0d1839' } }}
                >
                    Back to All Updates
                </Button>

                <Card elevation={0} sx={{ overflow: 'hidden', borderRadius: 3, border: '1px solid #e0e4e8' }}>
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        
                        <Box sx={{ mb: 3 }}>
                            <Typography 
                                variant="h3" 
                                sx={{ 
                                    fontWeight: 600, 
                                    color: '#11224d', 
                                    mb: 2,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                    lineHeight: 1.3,
                                    letterSpacing: '-0.01em',
                                    fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica", sans-serif'
                                }}
                            >
                                {selectedUpdate.heading}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                <Chip 
                                    icon={<EventIcon sx={{ fontSize: '0.9rem !important' }} />} 
                                    label={new Date(selectedUpdate.created_at).toLocaleDateString(undefined, { 
                                        year: 'numeric', month: 'long', day: 'numeric' 
                                    })} 
                                    size="small" 
                                    sx={{ 
                                        bgcolor: '#f0f4f8', 
                                        color: '#556987', 
                                        fontWeight: 600,
                                        fontSize: '0.8rem',
                                        height: 28,
                                        '& .MuiChip-icon': {
                                            color: '#556987'
                                        }
                                    }} 
                                />
                                {isNew(selectedUpdate.created_at) && (
                                    <Chip 
                                        icon={<NewReleasesIcon sx={{ fontSize: '0.9rem !important' }} />} 
                                        label="New" 
                                        size="small"
                                        sx={{ 
                                            bgcolor: '#ffb300',
                                            color: 'white',
                                            fontWeight: 700,
                                            fontSize: '0.75rem',
                                            height: 28,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            '& .MuiChip-icon': {
                                                color: 'white'
                                            }
                                        }} 
                                    />
                                )}
                            </Box>
                        </Box>

                        <Divider sx={{ mb: 3, borderColor: '#e8eaed' }} />

                        <Box sx={{ display: 'block' }}>
                            {mediaList.length > 0 && (
                                <Box
                                    sx={{
                                        float: { xs: 'none', md: 'right' },
                                        width: { xs: '100%', md: '450px' },
                                        ml: { md: 3 },
                                        mb: { xs: 2, md: 2 },
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                                        border: '1px solid #e0e4e8',
                                        position: 'relative'
                                    }}
                                >
                                   
                                    {hasMultipleMedia && !carouselPos.atStart && (
                                        <IconButton
                                            onClick={() => scrollCarousel(selectedUpdate.id, 'left')}
                                            sx={{
                                                position: 'absolute',
                                                left: 8,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                zIndex: 2,
                                                bgcolor: 'rgba(255, 255, 255, 0.95)',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                '&:hover': {
                                                    bgcolor: 'white',
                                                    transform: 'translateY(-50%) scale(1.1)'
                                                },
                                                width: 40,
                                                height: 40
                                            }}
                                        >
                                            <ChevronLeftIcon sx={{ color: '#11224d', fontSize: 28 }} />
                                        </IconButton>
                                    )}
                                    
                                    {hasMultipleMedia && !carouselPos.atEnd && (
                                        <IconButton
                                            onClick={() => scrollCarousel(selectedUpdate.id, 'right')}
                                            sx={{
                                                position: 'absolute',
                                                right: 8,
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                zIndex: 2,
                                                bgcolor: 'rgba(255, 255, 255, 0.95)',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                '&:hover': {
                                                    bgcolor: 'white',
                                                    transform: 'translateY(-50%) scale(1.1)'
                                                },
                                                width: 40,
                                                height: 40
                                            }}
                                        >
                                            <ChevronRightIcon sx={{ color: '#11224d', fontSize: 28 }} />
                                        </IconButton>
                                    )}

                                    <Box 
                                        id={`carousel-${selectedUpdate.id}`}
                                        onScroll={(e) => handleCarouselScroll(selectedUpdate.id, e)}
                                        sx={{ 
                                            display: 'flex', 
                                            overflowX: 'auto', 
                                            scrollSnapType: 'x mandatory',
                                            bgcolor: '#f8f9fa',
                                            scrollbarWidth: 'thin',
                                            '&::-webkit-scrollbar': { height: 6 },
                                            '&::-webkit-scrollbar-track': { 
                                                backgroundColor: '#e8eaed',
                                                borderRadius: 3
                                            },
                                            '&::-webkit-scrollbar-thumb': { 
                                                backgroundColor: '#ffb300', 
                                                borderRadius: 3,
                                                '&:hover': {
                                                    backgroundColor: '#e6a200'
                                                }
                                            }
                                        }}
                                    >
                                        {mediaList.map((media, idx) => (
                                            <Box 
                                                key={idx} 
                                                sx={{ 
                                                    minWidth: '100%', 
                                                    scrollSnapAlign: 'start', 
                                                    position: 'relative',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    bgcolor: '#f8f9fa'
                                                }}
                                            >
                                                {media.media_type === 'video' || (media.media_url && (media.media_url.endsWith('.mp4') || media.media_url.endsWith('.webm'))) ? (
                                                    <CardMedia
                                                        component="video"
                                                        controls
                                                        src={media.media_url.startsWith('http') ? media.media_url : `${API_BASE_URL}/${media.media_url}`}
                                                        sx={{ 
                                                            width: '100%',
                                                            maxHeight: '340px',
                                                            objectFit: 'contain'
                                                        }}
                                                    />
                                                ) : (
                                                    <CardMedia
                                                        component="img"
                                                        image={media.media_url.startsWith('http') ? media.media_url : `${API_BASE_URL}/${media.media_url}`}
                                                        alt={selectedUpdate.heading}
                                                        sx={{ 
                                                            width: '100%',
                                                            maxHeight: '340px',
                                                            objectFit: 'contain'
                                                        }}
                                                    />
                                                )}
                                                {hasMultipleMedia && (
                                                    <Chip
                                                        label={`${idx + 1} / ${mediaList.length}`}
                                                        size="small"
                                                        sx={{ 
                                                            position: 'absolute', 
                                                            bottom: 12, 
                                                            right: 12, 
                                                            bgcolor: 'rgba(17, 34, 77, 0.9)', 
                                                            color: 'white',
                                                            fontWeight: 700,
                                                            fontSize: '0.75rem',
                                                            height: 26,
                                                            backdropFilter: 'blur(4px)'
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    lineHeight: 1.8, 
                                    fontSize: '1.05rem',
                                    whiteSpace: 'pre-wrap',
                                    color: '#2d3748',
                                    mb: showReadMore ? 2 : 0,
                                    fontWeight: 400
                                }}
                            >
                                {displayContent}
                            </Typography>

                            {showReadMore && (
                                <Button
                                    onClick={() => toggleExpanded(selectedUpdate.id)}
                                    endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    sx={{
                                        color: '#11224d',
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        fontSize: '0.95rem',
                                        px: 2,
                                        py: 1,
                                        borderRadius: 2,
                                        '&:hover': {
                                            bgcolor: 'rgba(17, 34, 77, 0.06)'
                                        }
                                    }}
                                >
                                    {isExpanded ? 'Show Less' : 'Read More'}
                                </Button>
                            )}
                        </Box>

                        <Box sx={{ clear: 'both' }} />
                    </CardContent>
                </Card>
            </Container>
        );
    }

    // --- GRID VIEW ---
    return (
        <Container maxWidth="xl" sx={{ py: 6 }}>
            {error && <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>{error}</Alert>}

            <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    lg: 'repeat(3, 1fr)'
                },
                gap: 4
            }}>
                {updates.length === 0 && !error && (
                    <Box sx={{ gridColumn: '1/-1', py: 10, textAlign: 'center' }}>
                        <ImageIcon sx={{ fontSize: 80, color: '#cbd5e0', mb: 2 }} />
                        <Typography variant="h6" color="textSecondary">No updates available at the moment.</Typography>
                    </Box>
                )}

                {updates.map((update) => {
                    const mediaList = update.media || (update.media_url ? [{ media_url: update.media_url, media_type: update.media_type }] : []);
                    const mainMedia = mediaList[0];
                    const isUpdateVideo = mainMedia && (mainMedia.media_type === 'video' || (mainMedia.media_url && (mainMedia.media_url.endsWith('.mp4') || mainMedia.media_url.endsWith('.webm'))));

                    return (
                        <Card
                            key={update.id}
                            elevation={0}
                            onClick={() => setSelectedUpdate(update)}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                borderRadius: 4,
                                border: '1px solid #e2e8f0',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                '&:hover': {
                                    transform: 'translateY(-8px)',
                                    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                                    borderColor: '#ffb300'
                                }
                            }}
                        >
                            {/* Card Media Header */}
                            <Box sx={{ position: 'relative', height: 220, bgcolor: '#f7fafc' }}>
                                {mainMedia ? (
                                    <>
                                        <CardMedia
                                            component={isUpdateVideo ? "video" : "img"}
                                            image={!isUpdateVideo ? (mainMedia.media_url.startsWith('http') ? mainMedia.media_url : `${API_BASE_URL}/${mainMedia.media_url}`) : undefined}
                                            src={isUpdateVideo ? (mainMedia.media_url.startsWith('http') ? mainMedia.media_url : `${API_BASE_URL}/${mainMedia.media_url}`) : undefined}
                                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                        {isUpdateVideo && (
                                            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'rgba(255,179,0,0.9)', borderRadius: '50%', p: 1, display: 'flex', color: 'white' }}>
                                                <ChevronRightIcon fontSize="large" />
                                            </Box>
                                        )}
                                    </>
                                ) : (
                                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #edf2f7 0%, #e2e8f0 100%)' }}>
                                        <ImageIcon sx={{ color: '#cbd5e0', fontSize: 48 }} />
                                    </Box>
                                )}
                                <Chip 
                                    label={isUpdateVideo ? "VIDEO" : "ARTICLE"} 
                                    size="small" 
                                    sx={{ position: 'absolute', top: 16, right: 16, fontWeight: 800, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(4px)' }} 
                                />
                            </Box>

                            {/* Card Body */}
                            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                    <EventIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                                        {new Date(update.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </Typography>
                                </Box>

                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#11224d', mb: 1.5, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', height: '2.8em' }}>
                                    {update.heading}
                                </Typography>

                                <Typography variant="body2" sx={{ color: '#64748b', mb: 3, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.6 }}>
                                    {update.description || update.content}
                                </Typography>

                                <Box sx={{ mt: 'auto' }}>
                                    <Button 
                                        variant="contained" 
                                        fullWidth 
                                        disableElevation
                                        sx={{ 
                                            borderRadius: 2, 
                                            textTransform: 'none', 
                                            fontWeight: 700, 
                                            bgcolor: '#ffb300', 
                                            color: '#11224d',
                                            border: 'none',
                                            '&:hover': { bgcolor: '#e6a200' }
                                        }}
                                    >
                                        View Details
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>
        </Container>
    );
};

export default UpdatesAndTipsViewer;